import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { Breadcrumbs, Button, Link, Typography } from '@mui/material';
import { doc, getDoc } from 'firebase/firestore';
import { useTranslation } from 'react-i18next';

import { getLogo, getMediaImage } from '../../../api/aws/s3';
import { companiesRef } from '../../../api/firebase/firestore/company';
import { getMedias } from '../../../api/firebase/firestore/media';
import { getPostPreview } from '../../../api/microcms/article';
import {
  gray,
  primarycolor,
  secondaryButtonnBackground
} from '../../../constants/Colors';
import {
  breadcrumb,
  mypageHeader,
  mypageHeaderTitle,
  mypageWrapper
} from '../../../constants/Location';
import {
  categoryList,
  funnelList,
  mediaTypeList,
  publisherIndustry,
  targetList,
  targetTypeList
} from '../../../constants/Select';
import { screenType } from '../../../constants/enum';
import { throwError } from '../../../modules/error';
import CustomSnackbar from '../../uiParts/CustomSnackbar';
import MediaArticle from '../../uiParts/MediaArticle';

import type { SelectOption } from '../../../constants/Select';
import type {
  articleInfoType,
  mainItemType,
  infoItemType,
  pubInfoItemType
} from '../../uiParts/MediaArticle';

const styles = {
  aside: {
    display: 'flex',
    flex: 1,
    flexDirection: 'column'
  },
  breadcrumb,
  breadcrumbsText: {
    color: gray,
    fontSize: '12px'
  },
  button: {
    display: 'flex',
    margin: '11px auto',
    maxWidth: {
      sm: '372px',
      xs: '311px'
    }
  },
  headerButton: {
    ':hover': {
      border: 'none'
    },
    '@media screen and (max-width:480px)': {
      padding: '6px 8px'
    },
    border: 'none'
  },
  mypageHeader,
  mypageHeaderTitle,
  mypageWrapper,
  snackbar: {
    '@media screen and (max-width:960px)': {
      bottom: '24px',
      left: '20px',
      position: 'fixed',
      right: '20px'
    },
    bottom: '24px',
    display: 'block',
    left: '24px',
    position: 'absolute'
  },
  topContent: {
    flexGrow: 1,
    height: 'calc(100vh - 120px)',
    overflowY: 'scroll'
  }
};

const PublisherMypageArticlePreview: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const publisherIndustryList = publisherIndustry();
  const fList = funnelList();
  const tList = targetList();
  const tTypeList = targetTypeList();
  const cList = categoryList();
  const mTypeList = mediaTypeList();
  const [open, setOpen] = useState<boolean>(false);
  const [errorText, setErrorText] = useState<string>('');
  const [infoText, setInfoText] = useState<string>('');

  const [pubInfoItem, setPubInfoItem] = React.useState<pubInfoItemType>({
    companyName: '',
    industry: '',
    url: ''
  });
  const [mainItem, setMainItem] = React.useState<mainItemType>({
    caseStudy: '',
    feature: '',
    menu: '',
    message: '',
    outline: ''
  });
  const [articleInfo, setArticleInfo] = React.useState<articleInfoType>({
    created: '',
    face: null,
    image: null,
    labels: [''],
    publisherName: '',
    title: '',
    updated: ''
  });
  const [infoItem, setInfoItem] = React.useState<infoItemType>({
    funnel: '',
    genre: '',
    mediaName: '',
    overview: '',
    scale: '',
    target: '',
    targetType: '',
    type: ''
  });

  useEffect(() => {
    document.title = t('title.publisher.mediaArticle');
  });

  const formattedDate = (date: string | undefined): string | undefined => {
    if (date) {
      const d = new Date(date);
      const year = d.getFullYear();
      const month = d.getMonth() + 1;
      const day = d.getDate();
      return `${year}/${month}/${day}`;
    }
  };

  const showValueToName = (
    selectOption: SelectOption[],
    itemValue: number | undefined
  ): string => {
    const data = selectOption.find((element) => element.value === itemValue);
    return data?.option ?? '';
  };

  const showMediaType = (v: number): string => {
    const datas = mTypeList.map((item) =>
      item.options.find((i) => i.value === v)
    );
    const d = datas.filter((item) => item !== undefined);
    return d[0]?.option ?? '';
  };

  useEffect(() => {
    void (async () => {
      try {
        if (id) {
          // 記事内容取得
          const articleData = await getPostPreview(id);

          // メディア情報取得
          const mediaId = articleData.mediaId;
          const mediaData = await getMedias(mediaId ?? '');

          // 媒体社情報取得
          const companyId = articleData.companyId;
          const companyRef = doc(companiesRef, companyId);
          const companySnap = await getDoc(companyRef);

          // dataを表示用に整形
          const funnelArray = mediaData.funnel?.map((item: number) =>
            showValueToName(fList, item)
          );
          const targetArray = mediaData.target?.map((item: number) =>
            showValueToName(tList, item)
          );
          const categoryArray = mediaData.category?.map((item: number) =>
            showValueToName(cList, item)
          );
          const outline = articleData.content;
          const feature = articleData.feature;
          const menu = articleData.menu;
          const caseStudy = articleData.case;
          const message = articleData.message;
          const image = await getMediaImage(
            articleData.imagePath as unknown as string
          );
          const mediaType = showMediaType(mediaData.mediaType);

          const icon = await getLogo(companySnap);

          // 記事情報セット
          setArticleInfo({
            created: formattedDate(articleData.createdAt) ?? '',
            face: icon,
            image,
            labels: categoryArray,
            publisherName: companySnap.data()?.name as string,
            title: articleData.title ?? '',
            updated: formattedDate(articleData.updatedAt) ?? ''
          });

          // 記事内容セット
          setMainItem({
            caseStudy: caseStudy ?? '',
            feature: feature ?? '',
            menu: menu ?? '',
            message: message ?? '',
            outline: outline ?? ''
          });

          // 月間PV数のタイトルをメディア種別によって出し分け
          const pvTextSwitcher = (type: string): string => {
            let mediaTypeName;
            for (const group of mTypeList) {
              for (const option of group.options) {
                if (option.option === type) {
                  mediaTypeName = group.name;
                }
              }
            }
            if (
              mediaTypeName === t('media.mediaTypeOption.tv') ||
              mediaTypeName === t('media.mediaTypeOption.radio')
            ) {
              return t('media.scaleDetail.sacleNet');
            } else if (
              mediaTypeName === t('media.mediaTypeOption.paper') ||
              mediaTypeName === t('media.mediaTypeOption.magazine')
            ) {
              return t('media.scaleDetail.scaleCirculation');
            } else if (mediaTypeName === t('media.mediaTypeOption.web')) {
              return t('media.scaleDetail.scalePvMau');
            }
            return t('media.scaleDetail.scaleOoh');
          };

          // 月間UU数のタイトルをメディア種別によって出し分け
          const uuTextSwitcher = (type: string): string => {
            let mediaTypeName;
            for (const group of mTypeList) {
              for (const option of group.options) {
                if (option.option === type) {
                  mediaTypeName = group.name;
                }
              }
            }
            if (
              mediaTypeName === t('media.mediaTypeOption.tv') ||
              mediaTypeName === t('media.mediaTypeOption.radio')
            ) {
              return '';
            } else if (
              mediaTypeName === t('media.mediaTypeOption.paper') ||
              mediaTypeName === t('media.mediaTypeOption.magazine')
            ) {
              return t('media.scaleDetail.scalReaders');
            } else if (mediaTypeName === t('media.mediaTypeOption.web')) {
              return t('media.scaleDetail.scaleUu');
            }
            return '';
          };

          const showScaleData = [
            `${pvTextSwitcher(mediaType)} : ${mediaData.scalePvMau}`,
            ...(uuTextSwitcher(mediaType) !== ''
              ? [`${uuTextSwitcher(mediaType)} : ${mediaData.scaleUu ?? ''}`]
              : []),
            `${t('mediaArticle.members')} : ${mediaData.scaleMembers ?? ''}`
          ];

          // メディア情報セット
          setInfoItem({
            funnel: funnelArray ? funnelArray.join('、') : '',
            genre: categoryArray ? categoryArray.join('、') : '',
            mediaName: mediaData.name,
            overview: mediaData.overview,
            scale: showScaleData.join('\n'),
            target: targetArray ? targetArray.join('、') : '',
            targetType: showValueToName(tTypeList, mediaData.targetType),
            type: mediaType ?? ''
          });

          // 媒体社情報セット
          setPubInfoItem({
            companyName: companySnap.data()?.name as string,
            industry: showValueToName(
              publisherIndustryList,
              companySnap.data()?.industry as number
            ),
            url: companySnap.data()?.url as string
          });
        }
      } catch (e) {
        setErrorText(t('mypage.article.error'));
        throwError(e);
      }
    })();
  }, []);

  const breadcrumbs = [
    <Link
      underline="hover"
      key="1"
      href="/main/publisher/mypage/article"
      sx={styles.breadcrumbsText}
    >
      {t('mypage.article.list')}
    </Link>,
    <Link
      underline="hover"
      key="2"
      href={`/main/publisher/mypage/article/edit/${id ?? ''}`}
      sx={styles.breadcrumbsText}
    >
      {t('mypage.article.create')}
    </Link>,
    <Typography key="3" sx={styles.breadcrumbsText}>
      {t('mypage.article.preview')}
    </Typography>
  ];

  const backPage = (): void => {
    navigate(`/publisher/mypage/article/edit/${id ?? ''}`);
  };

  return (
    <>
      <Typography sx={styles.mypageWrapper} component="div">
        <Typography component="div" sx={styles.aside}>
          <Typography component="div">
            <Typography
              sx={[
                styles.mypageHeader,
                {
                  '@media screen and (max-width:480px)': {
                    alignItems: 'flex-start',
                    flexDirection: 'column',
                    height: '95px',
                    padding: '14px 16px 12px'
                  },
                  '@media screen and (max-width:960px)': {
                    top: 0
                  }
                }
              ]}
              component="div"
            >
              <Typography sx={styles.mypageHeaderTitle}>
                {t('mypage.article.preview')}
              </Typography>
              <Typography
                component="div"
                sx={{
                  '@media screen and (max-width:480px)': {
                    columnGap: '8px',
                    display: 'flex',
                    justifyContent: 'space-between',
                    width: '100%'
                  }
                }}
              >
                <Button
                  variant="outlined"
                  color="error"
                  sx={styles.headerButton}
                  onClick={() => {
                    void (() => {
                      backPage();
                    })();
                  }}
                >
                  {t('mypage.article.backEdit')}
                </Button>
                <Button
                  variant="contained"
                  sx={[secondaryButtonnBackground, { color: primarycolor }]}
                  onClick={() => {
                    void (async () => {
                      const url = window.location.href;
                      await navigator.clipboard.writeText(url);
                      setOpen(true);
                      setInfoText(t('mypage.article.successCopyUrl'));
                    })();
                  }}
                >
                  {t('mypage.article.copyUrl')}
                </Button>
              </Typography>
            </Typography>
            <Breadcrumbs
              separator=">"
              aria-label="breadcrumb"
              sx={styles.breadcrumb}
            >
              {breadcrumbs}
            </Breadcrumbs>
            <MediaArticle
              screen={screenType.publisher}
              articleInfo={articleInfo}
              mainItem={mainItem}
              infoItem={infoItem}
              pubInfoItem={pubInfoItem}
            />
          </Typography>
        </Typography>
      </Typography>
      <CustomSnackbar
        open={open}
        autoHideDuration={6000}
        onClose={() => setOpen(false)}
        text={errorText}
        infoText={infoText}
        sx={styles.snackbar}
      />
    </>
  );
};

export default PublisherMypageArticlePreview;
